import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { ArrowLeft, ExternalLink, Briefcase, MapPin, Building2, TrendingUp, Calendar, Users, Mail, Phone, ChevronDown, ChevronUp, Eye, Edit, Save, X, FileText } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface Term {
  id: number
  name: string
  slug: string
  hierarchy?: string
}

interface Application {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  city: string
  country: string | null
  languages: string[]
  technical_skills: string[]
  cv_url: string | null
  created_at: string | null
}

interface Job {
  id: number
  title: string
  status: string
  content: string
  engagement: string | null
  featured_image: string | null
  job_description_thumbnail: string | null
  job_description_url: string | null
  banner_image: string | null
  permalink: string | null
  experience_level: Term[]
  job_type: Term[]
  region: Term[]
  industries: Term[]
  business_unit: Term[]
  country: Term[]
  applications: Application[]
  created_at: string
  modified_at: string
}

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  
  // Taxonomy options state
  const [taxonomyOptions, setTaxonomyOptions] = useState<{
    experience_level: Term[]
    job_type: Term[]
    region: Term[]
    industries: Term[]
    business_unit: Term[]
    country: Term[]
  }>({
    experience_level: [],
    job_type: [],
    region: [],
    industries: [],
    business_unit: [],
    country: []
  })
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    status: '',
    engagement: '',
    job_description_url: '', // Will store PDF attachment ID
    job_description_url_preview: '', // For displaying the PDF URL
    featured_image: '', // Will store attachment ID
    featured_image_preview: '', // For displaying the image
    job_description_thumbnail: '', // Will store attachment ID
    job_description_thumbnail_preview: '', // For displaying the image
    banner_image: '', // Will store attachment ID
    banner_image_preview: '', // For displaying the image
    // Taxonomy fields (store term IDs)
    experience_level: [] as number[],
    job_type: [] as number[],
    region: [] as number[],
    industries: [] as number[],
    business_unit: [] as number[],
    country: [] as number[]
  })

  useEffect(() => {
    if (id) {
      fetchJob()
    }
  }, [id])
  
  const fetchTaxonomyTerms = async (taxonomy: string): Promise<Term[]> => {
    try {
      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/taxonomies/${taxonomy}`
      const headers: Record<string, string> = {}
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch ${taxonomy} terms`)
      }

      return await response.json()
    } catch (err) {
      console.error(`Error fetching ${taxonomy} terms:`, err)
      return []
    }
  }
  
  const fetchAllTaxonomies = async () => {
    const [experienceLevel, jobType, region, industries, businessUnit, country] = await Promise.all([
      fetchTaxonomyTerms('experience-level'),
      fetchTaxonomyTerms('job-type'),
      fetchTaxonomyTerms('region'),
      fetchTaxonomyTerms('industries'),
      fetchTaxonomyTerms('business-unit'),
      fetchTaxonomyTerms('country')
    ])
    
    setTaxonomyOptions({
      experience_level: experienceLevel,
      job_type: jobType,
      region,
      industries,
      business_unit: businessUnit,
      country
    })
  }

  const fetchJob = async () => {
    try {
      setLoading(true)
      setError(null)

      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      // Load job and taxonomies in parallel
      const [jobResponse] = await Promise.all([
        fetch(`${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/jobs/${id}`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {}
        }),
        // Load taxonomies once and cache in memory
        fetchAllTaxonomies()
      ])

      const result = await jobResponse.json()

      if (!jobResponse.ok) {
        throw new Error(result.message || 'Failed to fetch job details')
      }

      const wpConfig = (window as any).WP_COCKPIT
      const isDev = wpConfig?.devMode || false
      
      if (isDev) {
        console.log('Job data received:', result.data)
        console.log('Business Unit:', result.data.business_unit)
        console.log('Country:', result.data.country)
        console.log('Full result:', result)
        console.log('All taxonomy fields:', {
          experience_level: result.data.experience_level,
          job_type: result.data.job_type,
          region: result.data.region,
          industries: result.data.industries,
          business_unit: result.data.business_unit,
          country: result.data.country
        })
      }
      
      setJob(result.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job details')
      console.error('Error fetching job:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleEditClick = async () => {
    if (job) {
      // Taxonomies are already loaded when the job was fetched
      setEditForm({
        title: job.title,
        content: job.content,
        status: job.status,
        engagement: job.engagement || '',
        job_description_url: '', // Will be set when user uploads new PDF
        job_description_url_preview: job.job_description_url || '',
        featured_image: '', // Will be set when user uploads new image
        featured_image_preview: job.featured_image || '',
        job_description_thumbnail: '',
        job_description_thumbnail_preview: job.job_description_thumbnail || '',
        banner_image: '',
        banner_image_preview: job.banner_image || '',
        // Populate taxonomy fields with current term IDs
        experience_level: job.experience_level?.map(t => t.id) || [],
        job_type: job.job_type?.map(t => t.id) || [],
        region: job.region?.map(t => t.id) || [],
        industries: job.industries?.map(t => t.id) || [],
        business_unit: job.business_unit?.map(t => t.id) || [],
        country: job.country?.map(t => t.id) || []
      })
      setIsEditMode(true)
    }
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditForm({
      title: '',
      content: '',
      status: '',
      engagement: '',
      job_description_url: '',
      job_description_url_preview: '',
      featured_image: '',
      featured_image_preview: '',
      job_description_thumbnail: '',
      job_description_thumbnail_preview: '',
      banner_image: '',
      banner_image_preview: '',
      experience_level: [],
      job_type: [],
      region: [],
      industries: [],
      business_unit: [],
      country: []
    })
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, fieldName: string, fileType: 'image' | 'pdf') => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      const formData = new FormData()
      formData.append('file', file)

      const headers: Record<string, string> = {}
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(`${config.restNonce ? '/wp-json' : ''}/wp/v2/media`, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Failed to upload ${fileType}`)
      }

      const media = await response.json()
      
      // Save the attachment ID (as string for now, will be converted to number on save)
      // and the URL for preview
      setEditForm(prev => ({
        ...prev,
        [fieldName]: media.id.toString(),
        [`${fieldName}_preview`]: media.source_url
      }))

      toast({
        title: 'Success',
        description: `${fileType === 'pdf' ? 'PDF' : 'Image'} uploaded successfully`
      })
    } catch (err) {
      console.error(`Error uploading ${fileType}:`, err)
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : `Failed to upload ${fileType}`
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleSave = async () => {
    if (!job) return

    setIsSaving(true)
    try {
      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/jobs/${id}`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      // Only include fields that were actually changed (have attachment IDs)
      const payload: any = {
        title: editForm.title,
        content: editForm.content,
        status: editForm.status,
        engagement: editForm.engagement,
        // Taxonomy fields
        experience_level: editForm.experience_level,
        job_type: editForm.job_type,
        region: editForm.region,
        industries: editForm.industries,
        business_unit: editForm.business_unit,
        country: editForm.country
      }

      // job_description_url is a URL (from PDF upload), not an attachment ID
      if (editForm.job_description_url_preview) {
        payload.job_description_url = editForm.job_description_url_preview
      }
      
      // Only include image attachment fields if they were updated (have new IDs)
      if (editForm.featured_image) {
        payload.featured_image = parseInt(editForm.featured_image)
      }
      if (editForm.job_description_thumbnail) {
        payload.job_description_thumbnail = parseInt(editForm.job_description_thumbnail)
      }
      if (editForm.banner_image) {
        payload.banner_image = parseInt(editForm.banner_image)
      }

      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(payload)
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update job')
      }

      toast({
        title: 'Success',
        description: 'Job updated successfully'
      })

      setIsEditMode(false)
      fetchJob() // Refresh the job data
    } catch (err) {
      console.error('Error saving job:', err)
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update job'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'publish':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  const getEngagementColor = (engagement: string | null) => {
    if (!engagement) return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    
    switch (engagement.toLowerCase()) {
      case 'hybrid':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'remote':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      case 'temporary':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Spinner className="h-8 w-8" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Alert variant="destructive">
            <AlertDescription>{error || 'Job not found'}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {!isEditMode ? (
            <Button onClick={handleEditClick} variant="outline">
              <Edit className="h-4 w-4 mr-2" />
              Edit Job
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                onClick={handleSave} 
                disabled={isSaving || isUploading}
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button 
                onClick={handleCancelEdit} 
                variant="outline"
                disabled={isSaving || isUploading}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* Banner Image */}
        {job.banner_image && (
          <div className="mb-6 rounded-lg overflow-hidden">
            <img 
              src={job.banner_image} 
              alt={job.title}
              className="w-full h-64 object-cover"
            />
          </div>
        )}

        <div className="space-y-6">
          {/* Header Card */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {isEditMode ? (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="title">Job Title</Label>
                        <Input
                          id="title"
                          value={editForm.title}
                          onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                          placeholder="Job title"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="status">Status</Label>
                          <Select
                            value={editForm.status}
                            onValueChange={(value) => setEditForm({...editForm, status: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="publish">Published</SelectItem>
                              <SelectItem value="draft">Draft</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="engagement">Engagement Type</Label>
                          <Select
                            value={editForm.engagement}
                            onValueChange={(value) => setEditForm({...editForm, engagement: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select engagement" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Hybrid">Hybrid</SelectItem>
                              <SelectItem value="Remote">Remote</SelectItem>
                              <SelectItem value="Temporary">Temporary</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <CardTitle className="text-3xl mb-4">{job.title}</CardTitle>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={getStatusColor(job.status)}>
                          {job.status === 'publish' ? 'Published' : 'Draft'}
                        </Badge>
                        {job.engagement && (
                          <Badge className={getEngagementColor(job.engagement)}>
                            {job.engagement}
                          </Badge>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {isEditMode ? (
                  <div className="flex-shrink-0 space-y-2">
                    <Label htmlFor="featured_image">Featured Image</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="featured_image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'featured_image', 'image')}
                        disabled={isUploading}
                      />
                    </div>
                    {editForm.featured_image_preview && (
                      <img 
                        src={editForm.featured_image_preview} 
                        alt="Featured"
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                    )}
                  </div>
                ) : job.featured_image ? (
                  <div className="flex-shrink-0">
                    <img 
                      src={job.featured_image} 
                      alt={job.title}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">{isEditMode && (
                <>
                  <div>
                    <Label htmlFor="job_description_url">Job Description PDF</Label>
                    <Input
                      id="job_description_url"
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => handleFileUpload(e, 'job_description_url', 'pdf')}
                      disabled={isUploading}
                    />
                    {editForm.job_description_url_preview && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        <a href={editForm.job_description_url_preview} target="_blank" rel="noopener noreferrer" className="underline">
                          Current PDF
                        </a>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="banner_image">Banner Image</Label>
                    <Input
                      id="banner_image"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'banner_image', 'image')}
                      disabled={isUploading}
                    />
                    {editForm.banner_image_preview && (
                      <img 
                        src={editForm.banner_image_preview} 
                        alt="Banner"
                        className="w-full h-32 object-cover rounded-lg mt-2"
                      />
                    )}
                  </div>
                  
                  {/* Taxonomy Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="experience_level">Experience Level</Label>
                      <Select
                        value={editForm.experience_level[0]?.toString() || ''}
                        onValueChange={(value) => setEditForm({...editForm, experience_level: [parseInt(value)]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select experience level" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomyOptions.experience_level.map((term) => (
                            <SelectItem key={term.id} value={term.id.toString()}>
                              {term.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="job_type">Job Type</Label>
                      <Select
                        value={editForm.job_type[0]?.toString() || ''}
                        onValueChange={(value) => setEditForm({...editForm, job_type: [parseInt(value)]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select job type" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomyOptions.job_type.map((term) => (
                            <SelectItem key={term.id} value={term.id.toString()}>
                              {term.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="region">Region</Label>
                      <Select
                        value={editForm.region[0]?.toString() || ''}
                        onValueChange={(value) => setEditForm({...editForm, region: [parseInt(value)]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomyOptions.region.map((term) => (
                            <SelectItem key={term.id} value={term.id.toString()}>
                              {term.hierarchy || term.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Select
                        value={editForm.country[0]?.toString() || ''}
                        onValueChange={(value) => setEditForm({...editForm, country: [parseInt(value)]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomyOptions.country.map((term) => (
                            <SelectItem key={term.id} value={term.id.toString()}>
                              {term.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="industries">Industries</Label>
                      <Select
                        value={editForm.industries[0]?.toString() || ''}
                        onValueChange={(value) => setEditForm({...editForm, industries: [parseInt(value)]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomyOptions.industries.map((term) => (
                            <SelectItem key={term.id} value={term.id.toString()}>
                              {term.hierarchy || term.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="business_unit">Department</Label>
                      <Select
                        value={editForm.business_unit[0]?.toString() || ''}
                        onValueChange={(value) => setEditForm({...editForm, business_unit: [parseInt(value)]})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomyOptions.business_unit.map((term) => (
                            <SelectItem key={term.id} value={term.id.toString()}>
                              {term.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {!isEditMode && (
                <>
              {/* Meta Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{/* Experience Level */}
                {job.experience_level && job.experience_level.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span>Experience Level</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {job.experience_level.map((level) => (
                        <Badge key={level.id} variant="outline">
                          {level.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Job Type */}
                {job.job_type && job.job_type.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span>Job Type</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {job.job_type.map((type) => (
                        <Badge key={type.id} variant="outline">
                          {type.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location */}
                {(job.region && job.region.length > 0) || (job.country && job.country.length > 0) ? (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>Location</span>
                    </div>
                    <div className="space-y-1">
                      {job.region && job.region.length > 0 ? (
                        job.region.map((region) => {
                          const countryName = job.country && job.country.length > 0 ? job.country[0].name : '';
                          const locationText = countryName 
                            ? `${region.hierarchy || region.name}, ${countryName}`
                            : region.hierarchy || region.name;
                          return (
                            <div key={region.id} className="text-sm text-muted-foreground">
                              {locationText}
                            </div>
                          );
                        })
                      ) : (
                        job.country && job.country.length > 0 && (
                          <div className="text-sm text-muted-foreground">
                            {job.country[0].name}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ) : null}

                {/* Industries */}
                {job.industries && job.industries.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>Industries</span>
                    </div>
                    <div className="space-y-1">
                      {job.industries.map((industry) => (
                        <div key={industry.id} className="text-sm text-muted-foreground">
                          {industry.hierarchy || industry.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Department (Business Unit) */}
                {job.business_unit && job.business_unit.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>Department</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {job.business_unit.map((unit) => (
                        <Badge key={unit.id} variant="outline">
                          {unit.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="flex items-center gap-6 text-sm text-muted-foreground pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Posted: {formatDate(job.created_at)}</span>
                </div>
                {job.modified_at !== job.created_at && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Updated: {formatDate(job.modified_at)}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 flex flex-wrap gap-3">
                {job.status === 'publish' && job.permalink && (
                  <Button 
                    variant="default" 
                    size="lg"
                    onClick={() => window.open(job.permalink!, '_blank')}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Live Job
                  </Button>
                )}
                {job.job_description_url && (
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => window.open(job.job_description_url!, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Download Job Description
                  </Button>
                )}
              </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Job Description Card - Collapsible */}
          <Card>
            <Collapsible open={isDescriptionOpen} onOpenChange={setIsDescriptionOpen}>
              <CardHeader className="cursor-pointer" onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}>
                <div className="flex items-center justify-between">
                  <CardTitle>Job Description</CardTitle>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm">
                      {isDescriptionOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </CardHeader>
              <CollapsibleContent>
                <CardContent>
                  {isEditMode ? (
                    <div>
                      <Label htmlFor="content">Job Description</Label>
                      <ReactQuill
                        theme="snow"
                        value={editForm.content}
                        onChange={(value) => setEditForm({...editForm, content: value})}
                        className="bg-background"
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'indent': '-1'}, { 'indent': '+1' }],
                            ['link'],
                            ['clean']
                          ]
                        }}
                      />
                    </div>
                  ) : (
                    <div className="ql-editor" style={{ whiteSpace: 'normal' }}>
                      <div 
                        dangerouslySetInnerHTML={{ __html: job.content }}
                      />
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Additional Information - Job Description Thumbnail */}
          {(isEditMode || job.job_description_thumbnail) && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditMode ? (
                  <div>
                    <Label htmlFor="job_description_thumbnail">Job Description Thumbnail</Label>
                    <Input
                      id="job_description_thumbnail"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'job_description_thumbnail', 'image')}
                      disabled={isUploading}
                    />
                    {editForm.job_description_thumbnail_preview && (
                      <div className="max-w-3xl mx-auto mt-4">
                        <img 
                          src={editForm.job_description_thumbnail_preview} 
                          alt="Job description thumbnail"
                          className="w-full h-auto max-h-[600px] object-contain rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                ) : job.job_description_thumbnail ? (
                  <div className="max-w-3xl mx-auto">
                    <img 
                      src={job.job_description_thumbnail} 
                      alt="Job description thumbnail"
                      className="w-full h-auto max-h-[600px] object-contain rounded-lg"
                    />
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Applications Section */}
          {job.applications && job.applications.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Applications ({job.applications.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {job.applications.map((application) => (
                    <div
                      key={application.id}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/applications/${application.id}`)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">
                            {application.first_name} {application.last_name}
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5" />
                              <span>{application.email}</span>
                            </div>
                            {application.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{application.phone}</span>
                              </div>
                            )}
                            {application.city && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5" />
                                <span>{application.city}{application.country && `, ${application.country}`}</span>
                              </div>
                            )}
                            {application.created_at && (
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5" />
                                <span>Applied: {formatDate(application.created_at)}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {application.languages && application.languages.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {application.languages.slice(0, 3).map((lang, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {lang}
                                  </Badge>
                                ))}
                                {application.languages.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{application.languages.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}
                            {application.technical_skills && application.technical_skills.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {application.technical_skills.slice(0, 3).map((skill, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {skill}
                                  </Badge>
                                ))}
                                {application.technical_skills.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{application.technical_skills.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {application.cv_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(application.cv_url!, '_blank')
                            }}
                          >
                            <ExternalLink className="h-3.5 w-3.5 mr-1" />
                            CV
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
